import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Typography,
    Box,
    Divider,
} from '@mui/material';
import {
    Close as CloseIcon,
    FilterAlt as FilterIcon,
    RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format, addDays } from 'date-fns';

type FilterValues = {
    ini: string;
    end: string;
    source: string | null;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onApply: (filters: FilterValues) => void;
    initialFilters?: Partial<FilterValues>;
};

const defaultFilters: FilterValues = {
    ini: format(new Date(), 'yyyy-MM-dd'),
    end: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
    source: null,
};

export default function MshipBookingsFilterDialog({
    open,
    onClose,
    onApply,
    initialFilters = {},
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [filters, setFilters] = useState<FilterValues>({
        ...defaultFilters,
        ...initialFilters,
    });

    useEffect(() => {
        if (open) {
            setFilters({ ...defaultFilters, ...initialFilters });
        }
    }, [open, initialFilters]);

    const handleChange = (name: string, value: unknown) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleReset = () => {
        setFilters(defaultFilters);
    };

    const handleApply = () => {
        onApply(filters);
        onClose();
    };

    const getActiveFiltersCount = (): number => {
        let count = 0;
        if (filters.source) count++;
        // Period is always present but we count it if it's different from default
        const isDefaultPeriod =
            filters.ini === defaultFilters.ini &&
            filters.end === defaultFilters.end;
        if (!isDefaultPeriod) count++;
        return count;
    };

    const activeCount = getActiveFiltersCount();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterIcon color="primary" />
                    <Typography variant="h6" component="span" fontWeight={600}>
                        {t('generic.filters')}
                    </Typography>
                    {activeCount > 0 && (
                        <Box
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                borderRadius: '12px',
                                px: 1,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                            }}
                        >
                            {activeCount}
                        </Box>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
                <Grid container spacing={2}>
                    {/* Period - Start Date */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label={t('generic.startDate')}
                            value={filters.ini}
                            onChange={(e) =>
                                handleChange('ini', e.target.value)
                            }
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>

                    {/* Period - End Date */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label={t('generic.endDate')}
                            value={filters.end}
                            onChange={(e) =>
                                handleChange('end', e.target.value)
                            }
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>

                    {/* Source Filter */}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>{t('Source')}</InputLabel>
                            <Select
                                value={filters.source || ''}
                                label={t('Source')}
                                displayEmpty
                                onChange={(e) =>
                                    handleChange(
                                        'source',
                                        e.target.value || null,
                                    )
                                }
                            >
                                <MenuItem value="">
                                    <em>{t('generic.all')}</em>
                                </MenuItem>
                                <MenuItem value="SC">
                                    {t('Sportcenter')}
                                </MenuItem>
                                <MenuItem value="USER">{t('User')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{ px: 3, py: 2, justifyContent: 'space-between' }}
            >
                <Button
                    startIcon={<ResetIcon />}
                    onClick={handleReset}
                    color="inherit"
                >
                    {t('buttons.reset')}
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={onClose} color="inherit">
                        {t('generic.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        startIcon={<FilterIcon />}
                    >
                        {t('generic.apply')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
